// common components for heads up
import {
  isEndpointEntity,
  Entity,
  isFunctionEntity,
  isKafkaTopicEntity,
  DurationDetails,
  IssueValue,
  ValueIcon,
} from '@hud/portal-dtos/issues.dtos';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import React from 'react';
import { CellIcon, TrendLabel } from './styles';
import { LinkCell } from '@hud/web-components/Table/Table';
import { formatDuration, formatPercentage } from '@hud/web-components/utils';
import { Value } from '@hud/web-components/Trend/Trend.styles';
import { IconType } from '@hud/web-components/Icons/HudIcons';
import { Markdown } from '@hud/web-components/Markdown/Markdown';
import { getMethodsDisplayText } from '../../utils';

type HeadsUpEntityProps = {
  entity?: Entity;
  linkTo?: string;
  serviceId?: number;
  flowId?: number;
  globalUrlParams?: string;
};

export function entityText(entity?: Entity) {
  if (!entity) return '';
  if (isEndpointEntity(entity)) {
    // for endpoints, show the first method and count of any additional methods
    return `${getMethodsDisplayText(entity.methods)} ${entity.path}`;
  }
  if (isFunctionEntity(entity)) return entity.signature;
  if (isKafkaTopicEntity(entity)) return entity.name;
  return '';
}

export function entityIcon(entity?: Entity) {
  if (!entity) return 'other.codeBrackets';
  if (isEndpointEntity(entity)) return 'webviewMenu.endpoint';
  if (isFunctionEntity(entity)) return 'other.codeBrackets';
  if (isKafkaTopicEntity(entity)) return 'entities.messageQueue';
  return 'other.codeBrackets';
}

export function entityExploreSubpage(entity?: Entity) {
  if (!entity) return '';
  if (isEndpointEntity(entity)) return 'endpoints';
  if (isFunctionEntity(entity)) return 'functions';
  if (isKafkaTopicEntity(entity)) return 'messageQueues';
  return '';
}

export function serviceAndFlowParams(
  entity?: Entity,
  serviceId?: number,
  flowId?: number,
) {
  if (entity && !isEndpointEntity(entity) && serviceId && flowId) {
    return `&service=${serviceId}&flow=${flowId}`;
  }
  if (entity && isKafkaTopicEntity(entity) && serviceId) {
    return `&service=${serviceId}`;
  }
  return '';
}

export function HeadsUpEntity({
  entity,
  linkTo,
  serviceId,
  flowId,
  globalUrlParams,
}: HeadsUpEntityProps) {
  const entityId =
    entity && isEndpointEntity(entity) ? `${entity.id}-${serviceId}` : entity?.id;
  return (
    <LongContentPopover>
      <CellIcon icon={entityIcon(entity)} size={'20px'} />
      <LinkCell
        to={
          linkTo ||
          `/explore/${entityExploreSubpage(entity)}/${entityId}?${globalUrlParams}${serviceAndFlowParams(entity, serviceId, flowId)}`
        }
      >
        {entityText(entity)}
      </LinkCell>
    </LongContentPopover>
  );
}

type HeadsUpIncreaseProps = {
  durationDetails: DurationDetails;
  increaseIcon?: boolean;
};

export function HeadsUpDurationIncrease({
  durationDetails,
  increaseIcon = true,
}: HeadsUpIncreaseProps) {
  const icon = <CellIcon icon={'other.increase'} size={'20px'} />;

  const text = `${formatDuration(
    durationDetails?.previous,
  )} → ${formatDuration(durationDetails?.current)}`;

  const label =
    durationDetails && durationDetails.previous > 0
      ? `+${formatPercentage(
          (durationDetails?.current - durationDetails?.previous) /
            durationDetails?.previous,
        )}`
      : '';

  return (
    <LongContentPopover>
      {increaseIcon && icon} {text}
      <TrendLabel>
        <Value>{label}</Value>
      </TrendLabel>
    </LongContentPopover>
  );
}

type HeadsUpServiceProps = {
  serviceId?: number;
  serviceName?: string;
  globalUrlParams: string;
};

export function HeadsUpService({
  serviceId,
  serviceName,
  globalUrlParams,
}: HeadsUpServiceProps) {
  return (
    <LongContentPopover>
      <CellIcon icon="entities.servicesBlank" size={'20px'} />
      <LinkCell to={`/explore/services/${serviceId}?${globalUrlParams}`}>
        {serviceName}
      </LinkCell>
    </LongContentPopover>
  );
}

export function LegacyHeadsUpValue({ value }: { value: string }) {
  return (
    <LongContentPopover>
      <CellIcon icon="other.rightArrow" size="20px" />
      {value}
    </LongContentPopover>
  );
}

export function HeadsUpValue({ value }: { value: IssueValue }) {
  const iconMap: Record<ValueIcon, IconType> = {
    'right-arrow': 'other.rightArrow',
    trend: 'other.increase',
    flame: 'other.flame',
    info: 'other.info',
  };

  return (
    <LongContentPopover>
      <CellIcon icon={iconMap[value.icon] || 'other.rightArrow'} size="20px" />
      <Markdown>{value.content}</Markdown>
    </LongContentPopover>
  );
}

export type ConfigParamType = 'count' | 'milliseconds' | 'percentage';

export const getIcon = (type: ConfigParamType | undefined) => {
  switch (type) {
    case 'milliseconds':
      return 'metrics.stopWatch';
    case 'count':
    case 'percentage':
    default:
      return 'metrics.numberedList';
  }
};

export const getUnitString = (type: ConfigParamType | undefined): string | undefined => {
  switch (type) {
    case 'milliseconds':
      return 'ms';
    case 'percentage':
      return '%';
    case 'count':
    default:
      return undefined;
  }
};
